// BraceSidebar.tsx — Left panel for Brace Plan view.
// Panels grouped by elevation with collapsible sections.
// Override badge (●) marks panels with per-panel brace settings.

import { useState } from 'react';
import type { PanelData, BracePlanSettings } from '../types/panelvar';

export interface ElevationGroup {
  elevId: string;
  elevName: string;
  panels: PanelData[];
}

interface Props {
  groups: ElevationGroup[];
  checkedPanelIds: Set<string>;
  overrides: Record<string, Partial<BracePlanSettings>>;
  onToggle: (id: string) => void;
  onToggleElevation: (ids: string[], allChecked: boolean) => void;
  onSelectAll: () => void;
  onDeselectAll: () => void;
  collapsed?: boolean;
  onToggleCollapse?: () => void;
}

export default function BraceSidebar({
  groups,
  checkedPanelIds,
  overrides,
  onToggle,
  onToggleElevation,
  onSelectAll,
  onDeselectAll,
  collapsed,
  onToggleCollapse,
}: Props) {
  const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(new Set());

  function toggleGroup(elevId: string) {
    setCollapsedGroups(prev => {
      const next = new Set(prev);
      if (next.has(elevId)) next.delete(elevId); else next.add(elevId);
      return next;
    });
  }

  if (collapsed) {
    return (
      <div className="sidebar collapsed">
        <button className="sidebar-collapse-btn" onClick={onToggleCollapse} title="Expand sidebar">›</button>
      </div>
    );
  }

  const totalPanels = groups.reduce((n, g) => n + g.panels.length, 0);
  const allChecked = totalPanels > 0 && groups.every(g => g.panels.every(p => checkedPanelIds.has(p.id)));

  return (
    <div className="sidebar-inner">
      <div className="sidebar-header">
        <span className="sidebar-title">Panels</span>
        <button className="sidebar-collapse-btn" onClick={onToggleCollapse} title="Collapse sidebar">‹</button>
      </div>

      <div className="elev-sidebar-actions">
        <button className="elev-sidebar-btn" onClick={onSelectAll} disabled={allChecked}>Select All</button>
        <button className="elev-sidebar-btn" onClick={onDeselectAll} disabled={checkedPanelIds.size === 0}>Clear</button>
      </div>

      <ul className="elev-list" style={{ paddingBottom: 8 }}>
        {groups.map(g => {
          const groupIds = g.panels.map(p => p.id);
          const groupChecked = groupIds.filter(id => checkedPanelIds.has(id));
          const allGroupChecked = groupIds.length > 0 && groupChecked.length === groupIds.length;
          const someGroupChecked = groupChecked.length > 0 && !allGroupChecked;
          const isCollapsed = collapsedGroups.has(g.elevId);

          return (
            <li key={g.elevId} style={{ listStyle: 'none' }}>
              {/* ── Elevation header ── */}
              <div
                className={`elev-item elev-item-group${allGroupChecked ? ' checked' : ''}`}
                style={{ display: 'flex', alignItems: 'center', gap: 4, userSelect: 'none' }}
              >
                {/* Collapse toggle */}
                <button
                  style={{ background: 'none', border: 'none', color: '#aaa', cursor: 'pointer', padding: '0 2px', fontSize: 11, lineHeight: 1 }}
                  onClick={() => toggleGroup(g.elevId)}
                  title={isCollapsed ? 'Expand' : 'Collapse'}
                >
                  {isCollapsed ? '▶' : '▼'}
                </button>

                {/* Group checkbox */}
                <input
                  type="checkbox"
                  className="elev-checkbox"
                  checked={allGroupChecked}
                  ref={el => { if (el) el.indeterminate = someGroupChecked; }}
                  onChange={() => onToggleElevation(groupIds, allGroupChecked)}
                  onClick={e => e.stopPropagation()}
                  style={{ flexShrink: 0 }}
                />

                <span
                  className="elev-label"
                  style={{ flex: 1, cursor: 'pointer', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                  onClick={() => onToggleElevation(groupIds, allGroupChecked)}
                  title={g.elevName}
                >
                  {g.elevName}
                </span>
                <span style={{ color: '#666', fontSize: 10, flexShrink: 0 }}>
                  {groupChecked.length}/{g.panels.length}
                </span>
              </div>

              {/* ── Panels in this elevation ── */}
              {!isCollapsed && (
                <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
                  {g.panels.map(panel => {
                    const checked = checkedPanelIds.has(panel.id);
                    const hasOverride = Object.keys(overrides[panel.id] ?? {}).length > 0;
                    return (
                      <li
                        key={panel.id}
                        className={`elev-item elev-item-child${checked ? ' checked' : ''}`}
                        style={{ paddingLeft: 28, display: 'flex', alignItems: 'center', gap: 4 }}
                        onClick={() => onToggle(panel.id)}
                      >
                        <input
                          type="checkbox"
                          className="elev-checkbox"
                          checked={checked}
                          onChange={() => onToggle(panel.id)}
                          onClick={e => e.stopPropagation()}
                          style={{ flexShrink: 0 }}
                        />
                        <span className="elev-label" style={{ flex: 1 }}>
                          {panel.mpvar.panelMark || panel.id}
                          {hasOverride && (
                            <span title="Has per-panel brace overrides" style={{ marginLeft: 4, color: '#4da6ff', fontSize: 10 }}>●</span>
                          )}
                        </span>
                      </li>
                    );
                  })}
                </ul>
              )}
            </li>
          );
        })}

        {groups.length === 0 && (
          <li style={{ color: '#888', fontStyle: 'italic', padding: '8px 12px', listStyle: 'none' }}>
            No panels in project
          </li>
        )}
      </ul>
    </div>
  );
}
